import React from 'react';
import { Grid } from '@material-ui/core';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { convertToLocalTime } from '@dev/ee-tools';
import { CustomTableRowsProps, Typography } from '@dev/ee-components';
import { isEmpty, truncate } from 'lodash';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { DeliveryLogBadge } from '@api/components/DeliveryLogBadge';
import { logsBounceMessage } from '@api/services/dictionaries/LogsBounceMessage';
import { getAccount } from '@api/store/account/types';
import { getLogsStore } from '@api/store/logs/actions';
import { LogTypesString } from '@api/store/logs/types';
import { useMobile } from '@api/services/hooks/useMobile';

import { isLogsScreen } from '../helperFunctions';
import { UseLogsTableDataRowsInterface } from '../Interface';
import { EmailCell, HelpFont, LogsError } from '../assets/styledComponents';
import { MessageResult } from '../assets/MessageResult';

export const useLogTableRows = ({
    onRowIconClick,
    onRowEmailClick,
    onBounceDetailsClick,
}: UseLogsTableDataRowsInterface): CustomTableRowsProps[] => {
    const { logList, notificationList } = useSelector(getLogsStore);
    const { logType } = useParams<{ logType: LogTypesString }>();
    const isMobile = useMobile();
    const {
        GeneralInfo: { AccountAccessLevel },
    } = useSelector(getAccount);

    const initialList = isLogsScreen(LogTypesString.NOTIFICATION, logType) ? notificationList : logList;
    if (!initialList || isEmpty(initialList.Recipients)) {
        return [];
    }

    const isSmsLogScreen = isLogsScreen(LogTypesString.SMS, logType);
    const canSeeSingleContact = AccountAccessLevel.includes(AccessLevel.ViewContacts) && !isSmsLogScreen;

    return initialList?.Recipients?.map((recipient, index: number) => {
        const logErrorTitle = logsBounceMessage(recipient);
        const logErrorText = recipient.Message;
        const eventDate = recipient.EventDate || recipient.Date;

        if (isMobile) {
            return {
                id: recipient.MsgID,
                to: recipient.To,
                subject: recipient.Subject,
                event: <DeliveryLogBadge id={`status-${index}`} status={recipient.Status || recipient.EventType} />,
                ...(!!logErrorTitle &&
                    !!logErrorText && {
                        bouncetype: (
                            <LogsError clickable onClick={() => onBounceDetailsClick(logErrorTitle, logErrorText, recipient)}>
                                {logErrorTitle}
                            </LogsError>
                        ),
                    }),
            };
        }

        return {
            to: (
                <Grid container direction="column">
                    <EmailCell clickable={canSeeSingleContact} onClick={() => canSeeSingleContact && onRowEmailClick(recipient.To)}>
                        <Typography variant="caption" style={{ marginBottom: 2 }}>
                            {truncate(recipient.To, {
                                length: 66,
                            })}
                        </Typography>
                    </EmailCell>
                    <HelpFont variant="body1" bodyVariant="help">
                        {truncate(recipient.Subject, {
                            length: 66,
                        })}
                    </HelpFont>
                </Grid>
            ),
            createDate: (
                <Grid container direction="column">
                    <Typography variant="caption" style={{ marginBottom: 2 }}>
                        {convertToLocalTime(eventDate, 'L')}
                    </Typography>
                    <HelpFont variant="body1" bodyVariant="help">
                        {convertToLocalTime(eventDate, 'LT')}
                    </HelpFont>
                </Grid>
            ),
            event: <DeliveryLogBadge id={`status-${index}`} status={recipient.Status || recipient.EventType} />,
            ...(!isSmsLogScreen && {
                bouncetype: !!logErrorTitle && !!logErrorText && (
                    <Grid container direction="column">
                        <Typography variant="caption" style={{ marginBottom: 2 }}>
                            {recipient?.BounceType}
                        </Typography>
                        <HelpFont variant="body1" bodyVariant="help">
                            <LogsError onClick={() => onBounceDetailsClick(logErrorTitle, logErrorText, recipient)}>
                                {logErrorTitle}
                            </LogsError>
                        </HelpFont>
                    </Grid>
                ),
            }),
            icon: <MessageResult onClick={() => onRowIconClick(recipient.MsgID)} />,
        };
    });
};
